"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = ShareModal;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const clipboard_1 = __importDefault(require("@react-native-clipboard/clipboard"));
const useRequest_1 = __importDefault(require("../../hooks/useRequest"));
const useAppMessage_1 = require("../../hooks/useAppMessage");
const SocialIcon_1 = __importDefault(require("../SocialIcon"));
const AppButton_1 = require("../AppButton");
const AppText_1 = require("../AppText");
const colors_1 = require("../../common/colors");
const CloseButton_1 = __importDefault(require("../AppButton/CloseButton"));
const native_base_1 = require("native-base");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const pro_solid_svg_icons_1 = require("@fortawesome/pro-solid-svg-icons");
const randomPasswordGenerator_1 = require("../../services/randomPasswordGenerator");
const document_1 = require("../../services/document");
const error_1 = require("../../common/error");
const react_redux_1 = require("react-redux");
const document_2 = require("../../store/selectors/document");
const application_host_1 = require("../../common/application-host");
const AppTooltip_1 = __importDefault(require("../AppTooltip"));
const COPIED_LINK_RESPONSE_TIMEOUT = 3000;
const MIN_PASSWORD_LENGTH = 4;
function ShareModal({ isOpen, onClose, title, description, shareUrl, shareText, isPasswordSet, isAudioLoading, }) {
    const { request } = (0, useRequest_1.default)();
    const { sharedFileIds, sharedConversationId, sharingType } = (0, react_redux_1.useSelector)(document_2.selectDocumentShareState);
    const { showNotification } = (0, useAppMessage_1.useAppMessage)();
    const [isLinkCopied, setIsLinkCopied] = (0, react_1.useState)(false);
    const [requirePassword, setRequirePassword] = (0, react_1.useState)(false);
    const [password, setPassword] = (0, react_1.useState)('');
    const [showPassword, setShowPassword] = (0, react_1.useState)(false);
    const [isPasswordSetLocal, setIsPasswordSetLocal] = (0, react_1.useState)(false);
    const generatePassword = (0, react_1.useCallback)(async () => {
        const generatedPassword = await (0, randomPasswordGenerator_1.getRandomPassword)();
        setPassword(generatedPassword);
    }, []);
    (0, react_1.useEffect)(() => {
        if (!isOpen) {
            setRequirePassword(false);
            setPassword('');
            setShowPassword(false);
        }
    }, [isOpen]);
    (0, react_1.useEffect)(() => {
        if (isPasswordSet) {
            setRequirePassword(true);
        }
        setIsPasswordSetLocal(Boolean(isPasswordSet));
    }, [isPasswordSet]);
    const handleCopyPassword = (0, react_1.useCallback)(() => {
        clipboard_1.default.setString(password);
        showNotification('Password copied', '', 'success', COPIED_LINK_RESPONSE_TIMEOUT);
    }, [password]);
    const handleRegeneratePassword = (0, react_1.useCallback)(async () => {
        await generatePassword();
        showNotification('Password regenerated', '', 'success', COPIED_LINK_RESPONSE_TIMEOUT);
    }, [generatePassword]);
    const toggleRequirePassword = (0, react_1.useCallback)(() => {
        setRequirePassword(prev => {
            const newRequirePassword = !prev;
            if (!newRequirePassword) {
                setIsPasswordSetLocal(false);
                setPassword('');
            }
            return newRequirePassword;
        });
    }, [sharedConversationId, sharedFileIds]);
    const handleSetPassword = (0, react_1.useCallback)(async () => {
        if (password.length >= MIN_PASSWORD_LENGTH) {
            if (sharedConversationId && sharedFileIds) {
                const response = await (0, document_1.getShareLink)(sharedConversationId, sharedFileIds, password);
                if (response instanceof error_1.APIError || response instanceof error_1.RequestError) {
                    showNotification('Failed to set password', response.message, 'error', COPIED_LINK_RESPONSE_TIMEOUT);
                }
                else {
                    console.log("shareURL", response.url);
                    setIsPasswordSetLocal(true);
                    showNotification('Password set', '', 'success', COPIED_LINK_RESPONSE_TIMEOUT);
                }
            }
        }
        else {
            showNotification('Password must be at least 4 characters', '', 'error', COPIED_LINK_RESPONSE_TIMEOUT);
        }
    }, [password, sharedConversationId, sharedFileIds]);
    const handleResetPassword = (0, react_1.useCallback)(async () => {
        setIsPasswordSetLocal(false);
        setPassword('');
        request(document_1.getShareLink, sharedConversationId ?? '', sharedFileIds ?? [''], true);
        showNotification('Password protection removed. You can add a new password anytime.', '', 'success', COPIED_LINK_RESPONSE_TIMEOUT);
    }, [sharedConversationId, sharedFileIds, request]);
    const modifiedShareUrl = shareUrl.replace('https://docs.talktoo.ai', application_host_1.DOCS_HOST);
    const handleCopy = (0, react_1.useCallback)(() => {
        clipboard_1.default.setString(modifiedShareUrl);
        showNotification('Copied', '', 'success', COPIED_LINK_RESPONSE_TIMEOUT);
        setIsLinkCopied(true);
        setTimeout(() => {
            setIsLinkCopied(false);
        }, COPIED_LINK_RESPONSE_TIMEOUT);
    }, [modifiedShareUrl]);
    return ((0, jsx_runtime_1.jsx)(react_native_1.Modal, { visible: isOpen, transparent: true, children: (0, jsx_runtime_1.jsx)(native_base_1.NativeBaseProvider, { children: (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.background, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.container, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: "white", fontSize: 24, fontWeight: 700, children: title }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: colors_1.Colors.DARK_200, fontSize: 14, lineHeight: 24, children: description }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.url, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: "white", fontSize: 12, lineHeight: 20, style: { flex: 1, marginRight: 10 }, children: modifiedShareUrl }), (0, jsx_runtime_1.jsx)(AppButton_1.PrimaryButton, { isDisabled: isLinkCopied, width: 60, paddingY: 1.5, textProps: {
                                        fontSize: 12,
                                    }, onPress: handleCopy, children: isLinkCopied ? 'Copied' : 'Copy' })] }), isAudioLoading && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.loadingAudioContainer, children: [(0, jsx_runtime_1.jsx)(react_native_1.ActivityIndicator, { color: '#74C9E3', size: 16 }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.loadingAudioText, children: "SoundBite is getting generated. Will be available to hear once done." })] })), sharingType === 'owner' && !isPasswordSetLocal && ((0, jsx_runtime_1.jsxs)(react_native_1.TouchableOpacity, { onPress: toggleRequirePassword, style: styles.passwordToggle, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: colors_1.Colors.white, children: "Require Password" }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.checkboxContainer, children: requirePassword && ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.checkmark })) })] })), requirePassword && sharingType === 'owner' && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [!isPasswordSetLocal ? ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.passwordContainer, children: [(0, jsx_runtime_1.jsx)(native_base_1.Input, { value: password, onChangeText: setPassword, type: showPassword ? "text" : "password", isDisabled: isPasswordSetLocal, width: "84%", style: styles.passwordInput, placeholder: "Add a password", InputRightElement: !isPasswordSetLocal && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsx)(AppTooltip_1.default, { label: showPassword ? "Hide Password" : "Show Password", children: (0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { onPress: () => setShowPassword(!showPassword), style: [styles.eyeIcon, styles.iconHover], children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: showPassword ? pro_solid_svg_icons_1.faEyeSlash : pro_solid_svg_icons_1.faEye, size: 20, color: showPassword ? colors_1.Colors.white : colors_1.Colors.DARK_100 }) }) }), (0, jsx_runtime_1.jsx)(AppTooltip_1.default, { label: "Copy Password", children: (0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { onPress: handleCopyPassword, style: styles.copyIcon, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: pro_solid_svg_icons_1.faCopy, size: 20, color: colors_1.Colors.DARK_100 }) }) }), (0, jsx_runtime_1.jsx)(AppTooltip_1.default, { label: "Regenerate Password", children: (0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { onPress: handleRegeneratePassword, style: styles.regenerateIcon, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: pro_solid_svg_icons_1.faRotate, size: 20, color: colors_1.Colors.DARK_100 }) }) })] })) }), (0, jsx_runtime_1.jsx)(react_native_1.Pressable, { onPress: handleSetPassword, style: [
                                                styles.generateButton,
                                                password.length < MIN_PASSWORD_LENGTH && { opacity: 0.5 }
                                            ], disabled: password.length < MIN_PASSWORD_LENGTH, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: pro_solid_svg_icons_1.faCheck, size: 20, color: colors_1.Colors.DARK_700 }) })] })) : ((0, jsx_runtime_1.jsx)(react_native_1.Pressable, { onPress: handleResetPassword, style: styles.resetButton, children: (0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: colors_1.Colors.PRIMARY_300, children: "Reset Password" }) })), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: colors_1.Colors.DARK_200, fontSize: 12, style: styles.warningText, children: isPasswordSetLocal
                                        ? "Password set. You won't be able to see it again unless you reset. Anyone with the link and password can access."
                                        : "Once set, password can't be seen afterwards unless you reset. Anyone with the link and password can access." })] })), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.social, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { color: colors_1.Colors.DARK_200, lineHeight: 16, children: shareText }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.socialIcons, children: [(0, jsx_runtime_1.jsx)(SocialIcon_1.default, { socialMedia: "facebook", sharedUrl: modifiedShareUrl, size: 48 }), (0, jsx_runtime_1.jsx)(SocialIcon_1.default, { socialMedia: "reddit", sharedUrl: modifiedShareUrl, size: 48 }), (0, jsx_runtime_1.jsx)(SocialIcon_1.default, { socialMedia: "X", sharedUrl: modifiedShareUrl, size: 48 }), (0, jsx_runtime_1.jsx)(SocialIcon_1.default, { socialMedia: "email", sharedUrl: modifiedShareUrl, size: 48 })] })] }), (0, jsx_runtime_1.jsx)(CloseButton_1.default, { buttonSize: 24, iconSize: 12, onClose: onClose })] }) }) }) }));
}
const styles = react_native_1.StyleSheet.create({
    background: {
        backgroundColor: `${colors_1.Colors.DARK_700}CC`,
        flex: 1,
        padding: 15,
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        backgroundColor: colors_1.Colors.DARK_600,
        width: '100%',
        maxWidth: 480,
        gap: 16,
        padding: 24,
        borderRadius: 24,
        shadowColor: colors_1.Colors.PRIMARY_300,
        shadowOffset: { width: 30, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 24,
    },
    url: {
        backgroundColor: "#2A2A2F",
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderColor: colors_1.Colors.DARK_500,
        borderWidth: 1,
        borderRadius: 24,
    },
    loadingAudioContainer: {
        flexDirection: 'row',
        gap: 5,
    },
    loadingAudioText: {
        color: 'white',
        fontSize: 12,
        lineHeight: 20,
    },
    social: {
        gap: 8,
        marginTop: 16,
    },
    socialIcons: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    passwordToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    checkboxContainer: {
        width: 20,
        height: 20,
        borderRadius: 2,
        borderWidth: 2,
        borderColor: colors_1.Colors.PRIMARY_300,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkmark: {
        width: 12,
        height: 12,
        backgroundColor: colors_1.Colors.PRIMARY_300,
        borderRadius: 2,
        alignSelf: 'center',
    },
    passwordContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        marginTop: 10,
        flex: 1,
        backgroundColor: colors_1.Colors.DARK_700,
    },
    generateButton: {
        flex: 1,
        backgroundColor: colors_1.Colors.PRIMARY_300,
        padding: 10,
        borderRadius: 8,
        marginLeft: 10,
        alignItems: 'center',
    },
    resetButton: {
        backgroundColor: colors_1.Colors.PRIMARY_700,
        padding: 10,
        borderRadius: 8,
        alignItems: 'center',
        marginTop: 10,
        width: '100%',
    },
    eyeIcon: {
        padding: 10,
    },
    iconHover: {
        padding: 10,
    },
    copyIcon: {
        padding: 10,
    },
    regenerateIcon: {
        padding: 10,
    },
    warningText: {
        marginTop: 8,
        textAlign: 'left',
    },
    passwordInput: {
        backgroundColor: colors_1.Colors.DARK_700,
        marginLeft: 8,
        borderColor: colors_1.Colors.DARK_500,
        borderRadius: 8,
        flex: 1,
        color: colors_1.Colors.white,
        fontFamily: "'Courier New', Courier, monospace",
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
});
